import * as zod from 'zod';

import type { Robot } from '@sb/remote-control/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import type {
  FlangeOutputPortID,
  OutputPortID,
  RelayPortID,
} from '@sb/routine-runner';
import { IOLevel } from '@sb/routine-runner';

export namespace SetIOStep {
  export const name = 'Set I/O output';
  export const description =
    "Sets the robot's I/O interface, allowing it to send messages to external devices";
  export const librarySection = Step.LibrarySection.InterfaceWithMachines;
  export const librarySort = '1';
  export const argumentKind = 'SetIO';

  export const OutputConfiguration = zod.object({
    level: IOLevel,
    port: zod.number(),
    kind: zod.enum(['Output', 'Relay', 'FlangeOutput']).default('Output'),
  });

  export type OutputConfiguration = zod.infer<typeof OutputConfiguration>;

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    outputs: zod.array(OutputConfiguration).default([]),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    ioOutputs,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    const usedOutputs = ioOutputs.filter((output) =>
      args.outputs.some(
        (argOutput) =>
          argOutput.port === output.port && argOutput.kind === output.kind,
      ),
    );

    const usedSafetyIOs = usedOutputs.filter(
      (output) => output.assignedTo?.kind === 'safetyIO',
    );

    if (usedSafetyIOs.length > 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Safety I/O outputs cannot be set',
        fieldId: 'outputs',
      });
    }
  };

  export const getPortLabel = (output: OutputConfiguration) => {
    switch (output.kind) {
      case 'Output':
        return `Output ${output.port}` as OutputPortID;
      case 'Relay':
        return `Relay ${output.port}` as RelayPortID;
      case 'FlangeOutput':
        return `Flange Output ${output.port}` as FlangeOutputPortID;
      default:
        throw new TypeError(`Invalid port kind: ${output.kind}`);
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'SetIO',
      args: {
        changes: args.outputs.map((output) => {
          const label = getPortLabel(output);

          return { label, level: output.level, kind: output.kind };
        }),
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    robot,
    includeStepName,
  }) => {
    if (args?.argumentKind !== 'SetIO' || !robot) {
      return null;
    }

    const outputsDescriptions = args.outputs
      .map(({ kind, port, level }) => {
        let ioPort:
          | Robot.FlangeOutputPort
          | Robot.OutputPort
          | Robot.RelayPort
          | undefined;

        if (kind === 'Output') {
          ioPort = robot.ioOutputs.find((p) => p.port === port);
        } else if (kind === 'Relay') {
          ioPort = robot.ioRelays.find((p) => p.port === port);
        } else if (kind === 'FlangeOutput') {
          ioPort = robot.ioFlangeOutputs.find((p) => p.port === port);
        }

        if (ioPort == null) {
          return null;
        }

        const levelName =
          level === 'high' ? ioPort.highSignalName : ioPort.lowSignalName;

        return `${ioPort.name} to ${levelName}`;
      })
      .filter((p) => p != null);

    if (outputsDescriptions.length > 0) {
      return `${includeStepName ? 'Set ' : ''}${outputsDescriptions.join(', ')}`;
    }

    return null;
  };
}

SetIOStep satisfies Step.StepKindInfo;
